import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../../i18n/LanguageContext';
import Section from '../../components/ui/Section';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import { sendEmail } from '../../utils/emailService';
import { config } from '../../utils/config';
import { getActiveServices as getActiveServicesApi, createServiceRequest as createServiceRequestApi } from '../../utils/servicesAPI';
import { Helmet } from 'react-helmet';

const PageContainer = styled.div`
  padding-top: 70px;
`;

const HeroSection = styled.div`
  background-image: url('https://images.unsplash.com/photo-1560448204-603b3fc33ddc?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80');
  background-size: cover;
  background-position: center;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const HeroContent = styled.div`
  position: relative;
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: white;
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #111;
  color: #fff;
  width: 100%;
  max-width: 760px;
  border: 1px solid #333;
  border-radius: 16px;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  position: relative;
  height: 220px;
  background-size: cover;
  background-position: center;
`;
  
const ModalHeaderOverlay = styled.div`
    position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0,0,0,0.4), rgba(0,0,0,0.75));
`;

const ModalTitle = styled.h3`
  position: absolute;
  bottom: 1rem;
  left: 1.5rem;
  margin: 0;
  font-size: 1.75rem;
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  color: #a3a3a3;
`;

const Input = styled.input`
  background: #0b0b0b;
  border: 1px solid #333;
  color: #fff;
  border-radius: 10px;
  padding: 0.9rem 1rem;
`;

const TextArea = styled.textarea`
  background: #0b0b0b;
  border: 1px solid #333;
  color: #fff;
  border-radius: 10px;
  padding: 0.9rem 1rem;
  min-height: 110px;
  resize: vertical;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.8rem;
  right: 0.8rem;
  background: rgba(255,255,255,0.08);
  color: #fff;
  border: 1px solid #333;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
`;

const BuySell: React.FC = () => {
  const { language } = useLanguage();
  const [selectedItem, setSelectedItem] = React.useState<any | null>(null);
  const [clientName, setClientName] = React.useState('');
  const [clientEmail, setClientEmail] = React.useState('');
  const [clientPhone, setClientPhone] = React.useState('');
  const [requestedDate, setRequestedDate] = React.useState('');
  const [requestedTime, setRequestedTime] = React.useState('');
  const [participants, setParticipants] = React.useState(1);
  const [message, setMessage] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [submitSuccess, setSubmitSuccess] = React.useState<string | null>(null);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const handleOpen = (item: any) => {
    setSelectedItem(item);
    setSubmitSuccess(null);
    setSubmitError(null);
  };

  const handleClose = () => {
    setSelectedItem(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      const category: string | undefined = selectedItem.category;
      let mappedServiceId: string | null = null;
      if (category) {
        const { success, services } = await getActiveServicesApi(category);
        if (success && services && services.length > 0) {
          const itemTitle = (language === 'it' ? selectedItem.it : selectedItem.en).toLowerCase();
          const exact = services.find(s => (s.title_it || s.title_en).toLowerCase().includes(itemTitle.split(' ')[0]));
          mappedServiceId = (exact || services[0]).id;
        }
      }

      if (mappedServiceId) {
        const result = await createServiceRequestApi({
          serviceId: mappedServiceId,
          clientName,
          clientEmail,
          clientPhone,
          requestedDate,
          requestedTime,
          preferredTimeSlots: undefined,
          participantsCount: participants,
          message
        });
        if (!result.success) throw new Error(result.error || 'Errore creazione richiesta');
      } else {
        const subject = `Richiesta compravendite: ${language === 'it' ? selectedItem.it : selectedItem.en}`;
        const html = `
          <h2>Nuova richiesta compravendite</h2>
          <p><strong>Tipologia:</strong> ${language === 'it' ? selectedItem.it : selectedItem.en}</p>
          <p><strong>Nome:</strong> ${clientName}</p>
          <p><strong>Email:</strong> ${clientEmail}</p>
          <p><strong>Telefono:</strong> ${clientPhone || '-'}</p>
          <p><strong>Data:</strong> ${requestedDate || '-'}</p>
          <p><strong>Orario:</strong> ${requestedTime || '-'}</p>
          <p><strong>Partecipanti:</strong> ${participants}</p>
          <p><strong>Messaggio:</strong><br/>${message || '-'}</p>
        `;
        const text = `Nuova richiesta compravendite: ${(language === 'it' ? selectedItem.it : selectedItem.en)}\nNome: ${clientName}\nEmail: ${clientEmail}\nTelefono: ${clientPhone}\nData: ${requestedDate}\nOrario: ${requestedTime}\nPartecipanti: ${participants}\nMessaggio: ${message}`;
        const { success, error } = await sendEmail(config.app.supportEmail, { subject, html, text });
        if (!success) throw new Error(error || 'Errore invio email');
      }

      setSubmitSuccess(language === 'it' ? 'Richiesta inviata! Ti contatteremo a breve.' : 'Request sent! We will contact you soon.');
      setClientName(''); setClientEmail(''); setClientPhone(''); setRequestedDate(''); setRequestedTime(''); setParticipants(1); setMessage('');
    } catch (err: any) {
      setSubmitError(err.message || 'Errore imprevisto');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <PageContainer>
      <Helmet>
        <title>
          {language === 'it' 
            ? 'Compravendite Immobiliari | Servizi J-O-H' 
            : 'Real Estate Buy & Sell | J-O-H Services'}
        </title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Servizi di compravendita immobiliare: assistenza professionale per comprare o vendere proprietà di prestigio in Italia.'
            : 'Real estate buy and sell services: professional assistance to buy or sell prestigious properties in Italy.'}
        />
      </Helmet>
      
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            {language === 'it' ? 'Compravendite Immobiliari' : 'Real Estate Buy & Sell'}
          </HeroTitle>
          <HeroSubtitle>
            {language === 'it'
              ? 'Servizi professionali per acquistare o vendere proprietà di prestigio con tranquillità e sicurezza.'
              : 'Professional services to buy or sell prestigious properties with peace of mind and security.'}
          </HeroSubtitle>
          <Button variant="primary" to="/contact">
            {language === 'it' ? 'Richiedi Informazioni' : 'Request Information'}
          </Button>
        </HeroContent>
      </HeroSection>
      
      <Section padding="4rem 0">
        <Content>
          <CardsGrid>
            {[
              {
                it: 'Valutazioni Immobiliari',
                en: 'Property Valuations',
                category: 'real-estate',
                subtitleIt: 'Analisi di mercato',
                subtitleEn: 'Market analysis',
                descriptionIt: 'Stime accurate basate su dati e comparabili reali.',
                descriptionEn: 'Accurate estimates based on data and real comparables.',
                tags: ['Market', 'Analisi', 'Report'],
                price: 'su richiesta',
                image: 'https://images.unsplash.com/photo-1508387025470-549797097052?q=80&w=1350&auto=format&fit=crop'
              },
              {
                it: 'Ricerca Personalizzata',
                en: 'Personalized Search',
                category: 'real-estate',
                subtitleIt: 'Su misura',
                subtitleEn: 'Tailored',
                descriptionIt: 'Selezione di proprietà in linea con i tuoi criteri.',
                descriptionEn: 'Selection of properties aligned with your criteria.',
                tags: ['Buyer', 'Brief', 'Criteri'],
                price: 'su richiesta',
                image: 'https://images.unsplash.com/photo-1582408921715-18e7806360f0?q=80&w=1350&auto=format&fit=crop'
              },
              {
                it: 'Marketing Strategico',
                en: 'Strategic Marketing',
                category: 'real-estate',
                subtitleIt: 'Vendite rapide',
                subtitleEn: 'Faster sales',
                descriptionIt: 'Piani marketing per raggiungere acquirenti qualificati.',
                descriptionEn: 'Marketing plans to reach qualified buyers.',
                tags: ['Foto', 'Video', 'Campagne'],
                price: 'su richiesta',
                image: 'https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?q=80&w=1350&auto=format&fit=crop'
              },
              {
                it: 'Negoziazione & Contratti',
                en: 'Negotiation & Contracts',
                category: 'real-estate',
                subtitleIt: 'Tutela interessi',
                subtitleEn: 'Protect interests',
                descriptionIt: 'Gestione offerte e contrattualistica completa.',
                descriptionEn: 'Offers handling and full contractual support.',
                tags: ['Offerte', 'Contratti', 'Closing'],
                price: 'su richiesta',
                image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?q=80&w=1350&auto=format&fit=crop'
              },
              {
                it: 'Due Diligence',
                en: 'Due Diligence',
                category: 'real-estate',
                subtitleIt: 'Verifiche legali & tecniche',
                subtitleEn: 'Legal & technical checks',
                descriptionIt: 'Controlli accurati per transazioni sicure.',
                descriptionEn: 'Thorough checks for safe transactions.',
                tags: ['Legale', 'Tecnico', 'Sicurezza'],
                price: 'su richiesta',
                image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?q=80&w=1350&auto=format&fit=crop'
              },
              {
                it: 'Consulenza Investimenti',
                en: 'Investment Advisory',
                category: 'real-estate',
                subtitleIt: 'Rendimento & rischio',
                subtitleEn: 'Return & risk',
                descriptionIt: 'Analisi opportunità e strategie di investimento.',
                descriptionEn: 'Opportunity analysis and investment strategies.',
                tags: ['ROI', 'Strategia', 'Portfolio'],
                price: 'su richiesta',
                image: 'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?q=80&w=1350&auto=format&fit=crop'
              }
            ].map((x, idx) => (
              <Card
                key={x.it}
                image={x.image}
                title={language === 'it' ? x.it : x.en}
                subtitle={language === 'it' ? x.subtitleIt : x.subtitleEn}
                description={language === 'it' ? x.descriptionIt : x.descriptionEn}
                tags={x.tags}
                price={x.price}
                onClick={() => handleOpen(x)}
                hoverEffect={true}
                delay={idx * 0.05}
              />
            ))}
          </CardsGrid>
        </Content>
      </Section>
      
      {selectedItem && (
        <ModalOverlay onClick={handleClose}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <div style={{ position: 'relative' }}>
              <ModalHeader style={{ backgroundImage: `url(${selectedItem.image})` }}>
                <ModalHeaderOverlay />
                <ModalTitle>
                  {language === 'it' ? selectedItem.it : selectedItem.en}
                </ModalTitle>
                <CloseButton onClick={handleClose}>✕</CloseButton>
              </ModalHeader>
            </div>
            <ModalBody>
              <form onSubmit={handleSubmit}>
                <Row>
                  <Field>
                    <Label>{language === 'it' ? 'Nome completo' : 'Full name'}</Label>
                    <Input value={clientName} onChange={(e) => setClientName(e.target.value)} required />
                  </Field>
                  <Field>
                    <Label>Email</Label>
                    <Input type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} required />
                  </Field>
                </Row>
                <Row>
                  <Field>
                    <Label>{language === 'it' ? 'Telefono' : 'Phone'}</Label>
                    <Input value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} />
                  </Field>
                  <Field>
                    <Label>{language === 'it' ? 'Partecipanti' : 'Participants'}</Label>
                    <Input type="number" min={1} value={participants} onChange={(e) => setParticipants(parseInt(e.target.value || '1', 10))} />
                  </Field>
                </Row>
                <Row>
                  <Field>
                    <Label>{language === 'it' ? 'Data preferita' : 'Preferred date'}</Label>
                    <Input type="date" value={requestedDate} onChange={(e) => setRequestedDate(e.target.value)} />
                  </Field>
                  <Field>
                    <Label>{language === 'it' ? 'Orario preferito' : 'Preferred time'}</Label>
                    <Input type="time" value={requestedTime} onChange={(e) => setRequestedTime(e.target.value)} />
                  </Field>
                </Row>
                <Field>
                  <Label>{language === 'it' ? 'Messaggio' : 'Message'}</Label>
                  <TextArea value={message} onChange={(e) => setMessage(e.target.value)} placeholder={language === 'it' ? 'Esigenze, budget o preferenze...' : 'Requirements, budget or preferences...'} />
                </Field>
                {submitError && (
                  <div style={{ color: '#ef4444', marginTop: '0.75rem' }}>{submitError}</div>
                )}
                {submitSuccess && (
                  <div style={{ color: '#004744', marginTop: '0.75rem' }}>{submitSuccess}</div>
                )}
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                  <Button type="submit" variant="primary" disabled={submitting}>
                    {submitting ? (language === 'it' ? 'Invio...' : 'Sending...') : (language === 'it' ? 'Invia Richiesta' : 'Send Request')}
                  </Button>
                  <Button variant="outline" onClick={handleClose}>
                    {language === 'it' ? 'Annulla' : 'Cancel'}
                  </Button>
                </div>
              </form>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
      
    </PageContainer>
  );
};

export default BuySell; 